import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";
import { Avatar, Box, Checkbox, Group, Loader, Pagination, Table, Text, UnstyledButton } from "@mantine/core";
import React, { useMemo, useState } from "react";
import { formatShortDate } from "../../../utils/dateFormat";

export interface ReportResultMatch {
  /** What matched — a technology or certificate name. Full text, never truncated/joined with others. */
  label: string;
  /** Only certificates carry a date; omit for reports with no per-match date (e.g. Technology). */
  date?: string | null;
}

export interface ReportResultRow {
  personId: number;
  firstName: string;
  lastName: string;
  email: string;
  thumbnailBase64?: string | null;
  /** One or more matches for this person — each rendered as its own table row, never
   * concatenated into a single semicolon/comma-joined string (long names need real width). */
  matches: ReportResultMatch[];
}

export interface ReportResultsTableProps {
  rows: ReportResultRow[];
  loading?: boolean;
  selectedIds: Set<number>;
  onToggleSelect: (personId: number) => void;
  onSelectAllOnPage: () => void;
  onDeselectAllOnPage: () => void;
  allSelectedOnPage: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Column label for the "matched" column — e.g. "Certificate" or "Technology". */
  matchColumnLabel?: string;
}

type SortField = "name" | "matched" | "date";
type SortDirection = "asc" | "desc";

interface FlatDisplayRow {
  key: string;
  personId: number;
  firstName: string;
  lastName: string;
  email: string;
  thumbnailBase64?: string | null;
  match: ReportResultMatch;
}

interface SortableHeaderProps {
  field: SortField;
  label: string;
  sortField: SortField;
  sortDirection: SortDirection;
  onSortChange: (field: SortField) => void;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({ field, label, sortField, sortDirection, onSortChange }) => {
  const isActive = sortField === field;
  return (
    <UnstyledButton onClick={() => onSortChange(field)} aria-label={`Sort by ${label}`}>
      <Group gap={4} wrap="nowrap">
        <Text fw={600} size="sm">
          {label}
        </Text>
        {isActive &&
          (sortDirection === "asc" ? (
            <ChevronUpIcon style={{ width: 12, height: 12 }} />
          ) : (
            <ChevronDownIcon style={{ width: 12, height: 12 }} />
          ))}
      </Group>
    </UnstyledButton>
  );
};

/**
 * Paginated report results table (FS_05_Reporting_01). Flattens each person's matches into one
 * table row per match — never merges multiple matches into a single semicolon/comma-joined cell,
 * so long certificate/technology names get a real column instead of being crammed into a blob.
 * Sortable by Name / Matched / Date — client-side, over the rows already loaded on this page only
 * (no server-side sort parameter exists yet; this does not reorder across pages).
 */
export const ReportResultsTable: React.FC<ReportResultsTableProps> = ({
  rows,
  loading = false,
  selectedIds,
  onToggleSelect,
  onSelectAllOnPage,
  onDeselectAllOnPage,
  allSelectedOnPage,
  page,
  totalPages,
  onPageChange,
  matchColumnLabel = "Matched",
}) => {
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const handleSortChange = (field: SortField) => {
    if (field === sortField) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const showDateColumn = rows.some((r) => r.matches.some((m) => m.date));

  const flatRows = useMemo((): FlatDisplayRow[] => {
    const flattened = rows.flatMap((row) =>
      row.matches.map((match, index) => ({
        key: `${row.personId}-${index}`,
        personId: row.personId,
        firstName: row.firstName,
        lastName: row.lastName,
        email: row.email,
        thumbnailBase64: row.thumbnailBase64,
        match,
      }))
    );

    const direction = sortDirection === "asc" ? 1 : -1;
    return flattened.sort((a, b) => {
      switch (sortField) {
        case "matched":
          return a.match.label.localeCompare(b.match.label) * direction;
        case "date":
          return (a.match.date ?? "").localeCompare(b.match.date ?? "") * direction;
        case "name":
        default:
          return (
            (`${a.lastName} ${a.firstName}`).localeCompare(`${b.lastName} ${b.firstName}`) * direction
          );
      }
    });
  }, [rows, sortField, sortDirection]);

  if (loading) {
    return (
      <Box p="xl" style={{ textAlign: "center" }}>
        <Loader size="md" color="brand" />
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box p="xl" style={{ textAlign: "center" }}>
        <Text size="lg" fw={500} c="dimmed">
          No persons match this filter.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Table.ScrollContainer minWidth={520}>
        <Table verticalSpacing="sm" striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={44}>
                <Checkbox
                  checked={allSelectedOnPage}
                  onChange={() => (allSelectedOnPage ? onDeselectAllOnPage() : onSelectAllOnPage())}
                  aria-label="Select all on this page"
                />
              </Table.Th>
              <Table.Th w={44} />
              <Table.Th>
                <SortableHeader field="name" label="Name" sortField={sortField} sortDirection={sortDirection} onSortChange={handleSortChange} />
              </Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>
                <SortableHeader field="matched" label={matchColumnLabel} sortField={sortField} sortDirection={sortDirection} onSortChange={handleSortChange} />
              </Table.Th>
              {showDateColumn && (
                <Table.Th>
                  <SortableHeader field="date" label="Obtained" sortField={sortField} sortDirection={sortDirection} onSortChange={handleSortChange} />
                </Table.Th>
              )}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {flatRows.map((row) => {
              const isSelected = selectedIds.has(row.personId);
              const fullName = `${row.firstName} ${row.lastName}`;
              const initials = `${row.firstName.charAt(0)}${row.lastName.charAt(0)}`;
              return (
                <Table.Tr key={row.key} onClick={() => onToggleSelect(row.personId)} style={{ cursor: "pointer" }}>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => onToggleSelect(row.personId)}
                      aria-label={`Select ${fullName}`}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Avatar
                      size={32}
                      radius="xl"
                      color="brand"
                      src={row.thumbnailBase64 ? `data:image/jpeg;base64,${row.thumbnailBase64}` : undefined}
                    >
                      {!row.thumbnailBase64 && initials}
                    </Avatar>
                  </Table.Td>
                  <Table.Td>
                    <Text fw={500} style={{ whiteSpace: "nowrap" }}>
                      {fullName}
                    </Text>
                  </Table.Td>
                  <Table.Td>{row.email}</Table.Td>
                  <Table.Td style={{ whiteSpace: "normal", wordBreak: "normal" }}>{row.match.label}</Table.Td>
                  {showDateColumn && <Table.Td style={{ whiteSpace: "nowrap" }}>{formatShortDate(row.match.date)}</Table.Td>}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination value={page} onChange={onPageChange} total={totalPages} />
        </Group>
      )}
    </Box>
  );
};
